import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/20/solid";
import {
    EmailTemplateId,
    formatPhoneNumber,
    isPhoneNumberE164,
    toPhoneNumberE164,
    UserId,
    validEmail,
} from "@sp-crm/core";
import { useQuery } from "@tanstack/react-query";
import { trpc } from "components/rpc/trpc";
import { useActivityTypeOptions } from "components/tasks/activity-type-options";
import {
    AdvancedSearchEntityType,
    GetWorkflowSettingsResult,
    useGetTemplateQuery,
    WorkflowActorMode,
} from "generated/graphql";
import React, { useMemo, useState } from "react";
import { useUser } from "store/selectors/hooks";
import { getSanitizedMarkUpFromEmailBody } from "util/email";
import { stableQueryOptionsRpc } from "util/requests";
import { actionSettingKey, workflowActionSettingSuffix } from "./builder/builder-helper";
import {
    entityTypeNoun,
    getEmailRecipientTokens,
    getSmsRecipientTokens,
    resolveWorkflowSetting,
} from "./manage-workflows-helper";
import { EditableWorkflow } from "./types";

interface WorkflowActionSummaryProps {
    workflow: EditableWorkflow;
    workflowSettings: GetWorkflowSettingsResult;
}

interface ExpandableActionSummaryProps {
    summaryText: string;
    children: React.ReactNode;
}

const ExpandableActionSummary: React.FC<ExpandableActionSummaryProps> = ({
    summaryText,
    children,
}) => {
    const [expanded, setExpanded] = useState(false);

    return (
        <>
            <span
                className="cursor-pointer inline-flex items-center gap-1"
                onClick={() => setExpanded(!expanded)}>
                {summaryText}
                {expanded ? (
                    <ChevronUpIcon className="w-4 h-4 text-gray-500" />
                ) : (
                    <ChevronDownIcon className="w-4 h-4 text-gray-500" />
                )}
            </span>
            {expanded && (
                <dl className="mt-1 ml-4 text-gray-600 grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5">
                    {children}
                </dl>
            )}
        </>
    );
};

const UserName: React.FC<{ userId: UserId | null | undefined }> = ({ userId }) => {
    const user = useUser(userId);

    if (!user) {
        return <span>a specific user</span>;
    }

    return <span>{user.name}</span>;
};

const resolveRecipientDescription = (
    recipientValue: string | null,
    attachTo: AdvancedSearchEntityType,
): string => {
    if (!recipientValue) {
        return "configured recipients";
    }

    const recipientTokens = [
        ...getEmailRecipientTokens(attachTo),
        ...(attachTo === AdvancedSearchEntityType.Client
            ? [{ label: "Referral contact", token: "{{client.referralContact}}" }]
            : []),
    ];

    const recipients = recipientValue.split(",").map(r => r.trim());

    const labels: string[] = [];
    const literalEmails: string[] = [];

    for (const recipient of recipients) {
        const tokenMatch = recipientTokens.find(t => t.token === recipient);
        if (tokenMatch) {
            labels.push(tokenMatch.label.toLowerCase());
        } else if (validEmail(recipient)) {
            literalEmails.push(recipient);
        }
    }

    const allLabels = [...labels, ...literalEmails];
    if (allLabels.length === 0) {
        return "Configured recipients";
    }

    const result = allLabels.join(", ");
    return result.charAt(0).toUpperCase() + result.slice(1);
};

const resolveSmsRecipientDescription = (
    recipientValue: string | null,
    attachTo: AdvancedSearchEntityType,
): string => {
    if (!recipientValue) {
        return "Configured recipients";
    }

    const recipientTokens = getSmsRecipientTokens(attachTo);
    const recipients = recipientValue.split(",").map(r => r.trim());

    const labels: string[] = [];
    const literalNumbers: string[] = [];

    for (const recipient of recipients) {
        const tokenMatch = recipientTokens.find(t => t.token === recipient);
        if (tokenMatch) {
            labels.push(tokenMatch.label.toLowerCase());
        } else if (isPhoneNumberE164(recipient)) {
            literalNumbers.push(formatPhoneNumber(toPhoneNumberE164(recipient)));
        }
    }

    const allLabels = [...labels, ...literalNumbers];
    if (allLabels.length === 0) {
        return "Configured recipients";
    }

    const result = allLabels.join(", ");
    return result.charAt(0).toUpperCase() + result.slice(1);
};

type WorkflowAction = EditableWorkflow["actions"][number];

interface EmailActionSummaryProps {
    workflow: EditableWorkflow;
    action: WorkflowAction;
    workflowSettings: GetWorkflowSettingsResult;
}

const EmailActionSummary: React.FC<EmailActionSummaryProps> = ({
    workflow,
    action,
    workflowSettings,
}) => {
    const isAssignedUser = workflow.actorMode === WorkflowActorMode.AssignedUser;
    const suffix = workflowActionSettingSuffix(action);

    const templateId = useMemo(() => {
        return resolveWorkflowSetting(
            workflowSettings,
            workflow,
            actionSettingKey("emailTemplate", suffix),
        ) as EmailTemplateId | null;
    }, [workflowSettings, workflow, suffix]);

    const getTemplate = useGetTemplateQuery(
        { id: templateId },
        { enabled: !!templateId },
    );

    const recipientValue = resolveWorkflowSetting(
        workflowSettings,
        workflow,
        actionSettingKey("recipients", suffix),
    );

    const discreteToDescriptions: Record<string, string> = {
        referredUnselectedCommunities: "Each unchosen community that received a referral",
        allCommunityOptions: "All community options",
    };

    let recipientDescription: string;
    if (
        action?.__typename === "WorkflowActionEmailEntity" &&
        action.discreteTo &&
        discreteToDescriptions[action.discreteTo]
    ) {
        recipientDescription = discreteToDescriptions[action.discreteTo];
    } else if (recipientValue) {
        recipientDescription = resolveRecipientDescription(
            recipientValue,
            workflow.attachTo,
        );
    } else if (
        action?.__typename === "WorkflowActionEmailEntity" &&
        action.to === "{{client.referralContact}}"
    ) {
        recipientDescription = "Referral contact";
    } else {
        recipientDescription = "Configured recipients";
    }

    const templateSubject = getTemplate.data?.getTemplate?.template?.subject;
    const templateBody = getTemplate.data?.getTemplate?.template?.body;

    return (
        <ExpandableActionSummary
            summaryText={`Send email to ${recipientDescription.toLowerCase()}`}>
            <dt className="text-gray-500">To</dt>
            <dd>{recipientDescription}</dd>
            <dt className="text-gray-500">From</dt>
            <dd>
                {isAssignedUser ? (
                    `The ${entityTypeNoun(workflow.attachTo)}'s assigned user`
                ) : (
                    <UserName userId={workflow.defaultActorId} />
                )}
            </dd>
            {templateSubject && (
                <>
                    <dt className="text-gray-500">Subject</dt>
                    <dd>{templateSubject}</dd>
                </>
            )}
            {templateBody ? (
                <>
                    <dt className="text-gray-500">Body</dt>
                    <dd>
                        <div
                            className="unreset rounded border border-gray-200 p-2 text-sm"
                            dangerouslySetInnerHTML={getSanitizedMarkUpFromEmailBody(
                                templateBody,
                            )}
                        />
                    </dd>
                </>
            ) : null}
        </ExpandableActionSummary>
    );
};

interface TaskActionSummaryProps {
    workflow: EditableWorkflow;
    action: WorkflowAction;
    workflowSettings: GetWorkflowSettingsResult;
}

const TaskActionSummary: React.FC<TaskActionSummaryProps> = ({
    workflow,
    action,
    workflowSettings,
}) => {
    const taskTypes = useActivityTypeOptions();

    const taskTypeId =
        action?.__typename === "WorkflowActionTaskEntity" ? action.taskTypeId : null;

    const taskType = taskTypes.find(t => t.id === taskTypeId);

    const description = resolveWorkflowSetting(
        workflowSettings,
        workflow,
        actionSettingKey("description", workflowActionSettingSuffix(action)),
    );
    const cleanDescription = description?.trim() || null;

    const isAssignedUser = workflow.actorMode === WorkflowActorMode.AssignedUser;

    const summaryText = taskType?.name || cleanDescription || "task";

    return (
        <ExpandableActionSummary summaryText={`Create task: ${summaryText}`}>
            {taskType && (
                <>
                    <dt className="text-gray-500">Type</dt>
                    <dd>{taskType.name}</dd>
                </>
            )}
            {cleanDescription && (
                <>
                    <dt className="text-gray-500">Description</dt>
                    <dd>{cleanDescription}</dd>
                </>
            )}
            <dt className="text-gray-500">Assigned to</dt>
            <dd>
                {isAssignedUser ? (
                    `The ${entityTypeNoun(workflow.attachTo)}'s assigned user`
                ) : (
                    <UserName userId={workflow.defaultActorId} />
                )}
            </dd>
        </ExpandableActionSummary>
    );
};

interface SmsActionSummaryProps {
    workflow: EditableWorkflow;
    action: WorkflowAction;
    workflowSettings: GetWorkflowSettingsResult;
}

const SmsActionSummary: React.FC<SmsActionSummaryProps> = ({
    workflow,
    action,
    workflowSettings,
}) => {
    const suffix = workflowActionSettingSuffix(action);
    const recipientValue = resolveWorkflowSetting(
        workflowSettings,
        workflow,
        actionSettingKey("smsRecipients", suffix),
    );
    const recipientDescription = resolveSmsRecipientDescription(
        recipientValue,
        workflow.attachTo,
    );
    const body = resolveWorkflowSetting(
        workflowSettings,
        workflow,
        actionSettingKey("smsBody", suffix),
    );
    const fromPhoneLineId =
        action?.__typename === "WorkflowActionSmsEntity" ? action.fromPhoneLineId : null;
    const trpcClient = trpc.useTRPC();
    const phoneLinesQuery = useQuery({
        ...trpcClient.telecom.listWorkflowPhoneLines.queryOptions({
            workflowId: workflow.id,
        }),
        ...stableQueryOptionsRpc(),
        enabled: !!fromPhoneLineId,
    });
    const fromLine = (phoneLinesQuery.data?.phoneLines ?? []).find(
        line => line.id === fromPhoneLineId,
    );

    return (
        <ExpandableActionSummary
            summaryText={`Send text message to ${recipientDescription.toLowerCase()}`}>
            <dt className="text-gray-500">To</dt>
            <dd>{recipientDescription}</dd>
            {!fromPhoneLineId ? (
                <>
                    <dt className="text-gray-500">From</dt>
                    <dd>Not chosen yet</dd>
                </>
            ) : fromLine ? (
                <>
                    <dt className="text-gray-500">From</dt>
                    <dd>
                        {formatPhoneNumber(fromLine.phoneNumber)}
                        {fromLine.ownerName ? ` (${fromLine.ownerName})` : ""}
                    </dd>
                </>
            ) : null}
            <dt className="text-gray-500">Message</dt>
            <dd className="whitespace-pre-wrap">{body || "Not written yet"}</dd>
        </ExpandableActionSummary>
    );
};

interface SingleActionSummaryProps {
    workflow: EditableWorkflow;
    action: WorkflowAction;
    workflowSettings: GetWorkflowSettingsResult;
}

export const SingleActionSummary: React.FC<SingleActionSummaryProps> = ({
    workflow,
    action,
    workflowSettings,
}) => {
    switch (action.__typename) {
        case "WorkflowActionEmailEntity":
            return (
                <EmailActionSummary
                    workflow={workflow}
                    action={action}
                    workflowSettings={workflowSettings}
                />
            );
        case "WorkflowActionSmsEntity":
            return (
                <SmsActionSummary
                    workflow={workflow}
                    action={action}
                    workflowSettings={workflowSettings}
                />
            );
        case "WorkflowActionTaskEntity":
            return (
                <TaskActionSummary
                    workflow={workflow}
                    action={action}
                    workflowSettings={workflowSettings}
                />
            );
        default: {
            const exhaustiveCheck: never = action;
            throw new Error(
                `Unhandled workflow action ${JSON.stringify(exhaustiveCheck)}`,
            );
        }
    }
};

export const WorkflowActionSummary: React.FC<WorkflowActionSummaryProps> = ({
    workflow,
    workflowSettings,
}) => {
    const actions = workflow.actions ?? [];

    if (actions.length === 0) {
        return null;
    }

    return (
        <div className="space-y-1">
            {actions.map((action, index) => (
                <div key={action.id ?? index}>
                    <SingleActionSummary
                        workflow={workflow}
                        action={action}
                        workflowSettings={workflowSettings}
                    />
                </div>
            ))}
        </div>
    );
};
